import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import { omit } from "lodash";

import InputFieldText from "Components/Common/InputFieldText";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";
import {
  SUPER_ADMIN,
  powerAdmin2Roles,
  powerAdmin3Roles,
} from "common/constant";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { filterEmptyStrings, isAuthorizedUser, onlyNumberKeys } from "utils";

import {
  useGetApplicationRefundCalculations,
  usePostRefundCalculationMutation,
  usePutRefundCalculationMutation,
} from "apis/ApplicationRequestDetail";

const PermanentConnectionRefundCalculations = ({
  applicationId = "",
  utilityConsumerId,
  t,
  handleClose,
}) => {
  const [addRefundCalculations, { isLoading }] =
    usePostRefundCalculationMutation();

  const { roles } = useSelector((state) => state.Login);

  const [updateRefundCalculations, { isLoading: isRefundCalculationUpdating }] =
    usePutRefundCalculationMutation();

  const {
    data: { data: refundCalculationsData } = {},
    isLoading: isRefundCalculationDataLoading,
  } = useGetApplicationRefundCalculations(
    filterEmptyStrings({
      applicationId,
      utilityConsumerId,
    })
  );

  const isDisabled = useMemo(
    () => isAuthorizedUser(roles, powerAdmin2Roles),
    [roles]
  );

  const {
    consumerNumber,
    consumerName,
    address,
    dateOfConnection,
    dateOfDisconnection,
    serviceActivePeriod,
    remainingMonths,
  } = refundCalculationsData?.utilityConsumerDetails || {};
  const initialValues = useMemo(
    () => ({
      applicationId: refundCalculationsData?.applicationId ?? applicationId,
      utilityConsumerId,
      demandCharges: 0,
      refundCalculation: {
        id: refundCalculationsData?.refundCalculation?.id ?? 0,
        disconnectionCharge:
          refundCalculationsData?.refundCalculation?.disconnectionCharge ?? "",
        securityDeposit:
          refundCalculationsData?.refundCalculation?.securityDeposit ?? "",
        finalAmount:
          refundCalculationsData?.refundCalculation?.finalAmount ?? "",
        pendingDues:
          refundCalculationsData?.refundCalculation?.pendingDues ?? "",
        demandCharges:
          refundCalculationsData?.utilityConsumerDetails?.demandCharges ?? "",
        recoveredAmount:
          refundCalculationsData?.refundCalculation?.recoveredAmount ?? "",
        billAmount:
          refundCalculationsData?.utilityConsumerDetails?.billAmount ?? 0,
      },
    }),
    [refundCalculationsData]
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      refundCalculation: Yup.object({
        demandCharges: Yup.number().required("Demand charges is required"),
        disconnectionCharge: Yup.number().required(
          "Disconnection charges is required"
        ),
        securityDeposit: Yup.number().required("Security deposit is required"),
      }),
    }),
    onSubmit: async (values) => {
      try {
        if (roles.includes(SUPER_ADMIN)) {
          await updateRefundCalculations(
            filterEmptyStrings(
              omit(values, ["demandCharges", "refundCalculation.billAmount"])
            )
          );
        } else {
          await addRefundCalculations(
            filterEmptyStrings(
              omit(values, ["demandCharges", "refundCalculation.billAmount"])
            )
          );
        }
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: {
      refundCalculation: {
        disconnectionCharge,
        securityDeposit,
        pendingDues,
        demandCharges,
        recoveredAmount,
        billAmount,
      },
    },
    setFieldValue,
    handleSubmit,
  } = formik;

  useEffect(() => {
    setFieldValue(
      "refundCalculation.finalAmount",
      +(
        securityDeposit -
        disconnectionCharge -
        recoveredAmount -
        pendingDues +
        billAmount
      ).toFixed(2)
    );
  }, [
    disconnectionCharge,
    securityDeposit,
    pendingDues,
    recoveredAmount,
    billAmount,
  ]);

  useEffect(() => {
    setFieldValue(
      "refundCalculation.recoveredAmount",
      +(+demandCharges * +remainingMonths)
    );
  }, [demandCharges, remainingMonths]);

  useEffect(() => {
    if (remainingMonths <= 24) {
      setFieldValue("demandCharges", +recoveredAmount);
    }
  }, [recoveredAmount, remainingMonths]);

  return (
    <>
      {isRefundCalculationDataLoading ? (
        <div className="d-flex justify-content-center mt-3">
          <CircularProgress size={30} />
        </div>
      ) : (
        <>
          <Grid
            container
            columnSpacing={1}
            className="border border-2 ps-2 pb-2 p-3 mb-2 mt-2"
          >
            <Grid container columnSpacing={4}>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Consumer Number")}: </b>
                  {consumerNumber}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Consumer Name")}: </b>
                  {consumerName}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Date Of Connection")}: </b>
                  {convertUTCDateToLocalDate(dateOfConnection).format(
                    "DD-MM-YYYY"
                  )}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Date Of Disconnection")}: </b>
                  {convertUTCDateToLocalDate(dateOfDisconnection).format(
                    "DD-MM-YYYY"
                  )}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Address")}: </b>
                  {address}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Service Active Period")}: </b>
                  {serviceActivePeriod}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Remaining Months")}: </b>
                  {remainingMonths}
                </Text>
              </Grid>

              <Grid item xs={12} md={6}>
                <Text>
                  <b>{t("Amount To Be Recovered From Consumer")}: </b>
                  {recoveredAmount}
                </Text>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("Security Deposit") + "*"}
                name="refundCalculation.securityDeposit"
                formik={formik}
                disabled={isDisabled}
                placeholder="Enter Security Deposit"
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("Demand Charges") + "*"}
                name="refundCalculation.demandCharges"
                formik={formik}
                placeholder="Enter Demand Charges"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputFieldText
                label={t("Disconnection Charges") + "*"}
                name="refundCalculation.disconnectionCharge"
                formik={formik}
                placeholder="Enter Disconnection Charges"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                disabled={isDisabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputFieldText
                label={t(
                  "Demand charges to be recovered if disconnected before 24 months"
                )}
                name="demandCharges"
                formik={formik}
                placeholder="Demand charges to be recovered if disconnected before 24 months"
                type="number"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputFieldText
                label={t("Pending Dues if any")}
                name="refundCalculation.pendingDues"
                formik={formik}
                placeholder="Enter Pending Dues if any"
                disabled={isAuthorizedUser(roles, powerAdmin3Roles)}
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputFieldText
                label={t("Bill Amount")}
                name="refundCalculation.billAmount"
                formik={formik}
                placeholder="Bill Amount"
                type="number"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputFieldText
                label={t("Final amount to be refunded to customer")}
                name="refundCalculation.finalAmount"
                formik={formik}
                placeholder="Final amount to be refunded to customer"
                type="number"
                disabled
              />
            </Grid>
          </Grid>
          <Box className="d-flex justify-content-end">
            <Button
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || isRefundCalculationUpdating}
            >
              {t("Submit")}
              {(isLoading || isRefundCalculationUpdating) && (
                <CircularProgress size={20} />
              )}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default withTranslation()(PermanentConnectionRefundCalculations);

PermanentConnectionRefundCalculations.propTypes = {
  t: PropTypes.func,
  applicationId: PropTypes.number,
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
};
